import React, { useState, useCallback, useMemo } from 'react';
import { Table, Spin, Space } from 'antd';
import { Link, useLocation } from 'react-router-dom';
import moment from 'moment';
import { useSupporFiltertData } from '../../../../../../Hooks/TT_ELD/Supports';
import SupportTable from './SupportTable';
import { SearchResultForCompany } from '../../../../../../Utils/SearchResults';
import SearchOptions from '../../../../../../Utils/SearchOptions';
import { calcOrder } from '../../../../../components/core/calcOrder';

interface SupportLayoutProps {
  company: any[];
  isFetchingCMP: boolean;
  isLoadingCMP: boolean;
  onChangeCMP: (current: any) => void;
  total: number;
  skip2: any | number;
}

interface CompanyTableItem {
  id: string | number;
  companyId?: string | number;
  name?: string;
  checked_date?: string;
  company?: {
    name: string;
  };
}

interface NameColumnData {
  no: number;
  id: string | number;
  name: string | null;
  updatedAt: string | null;
}

const SupportLayout: React.FC<SupportLayoutProps> = ({
  company = [],
  isFetchingCMP,
  isLoadingCMP,
  onChangeCMP,
  total,
  skip2,
}) => {
  const location = useLocation();
  const [companyId, setCompanyId] = useState<string | number | any>();
  const [supportPage, setSupportPage] = useState(1);

  const isTopErrorsPage = location.pathname === '/errors/top-errors';

  const {
    data: supportData,
    isFetching,
    isLoading,
  }: any = useSupporFiltertData(companyId, undefined, supportPage);

  const handleSupportPagination = useCallback((pagination: any) => {
    setSupportPage(parseInt(pagination.current) || 1);
  }, []);

  const handleCompanySelect = useCallback((_: any, { valId }: { valId?: number }) => {
    setCompanyId(valId);
    setSupportPage(1);
  }, []);

  const companyColumns = useMemo(
    () => [
      {
        title: 'Company',
        dataIndex: 'name',
        key: 'name',
        width: 450,
        render: (data: NameColumnData) => (
          <Link to={`${data.id}`}>
            {data.no} &nbsp;&nbsp;
            {data.name} &nbsp;&nbsp;
            {data.updatedAt && <span>{data.updatedAt}</span>}
          </Link>
        ),
      },
    ],
    [],
  );

  const companyDataSource = useMemo(
    () =>
      company.map((item: CompanyTableItem, index: number) => {
        const isTopErrorItem = isTopErrorsPage && item.company;

        return {
          key: item.id,
          name: {
            no: calcOrder(skip2, 100, index),
            id: isTopErrorsPage ? item.companyId : item.id,
            name: isTopErrorItem ? item.company!.name : item.name || 'N/A',
            updatedAt: isTopErrorsPage
              ? null
              : item.checked_date
              ? moment(item.checked_date).format('YYYY-MM-DD, h:mm:ss a')
              : 'No date',
          },
        };
      }),
    [company, skip2, isTopErrorsPage],
  );

  const containerStyle: React.CSSProperties = {
    margin: '24px 0 0 0',
    display: 'flex',
    flexDirection: 'row',
    gap: 20,
  };

  const tableContainerStyle: React.CSSProperties = {
    boxShadow: 'rgba(0, 0, 0, 0.18) 0px 2px 4px',
    width: '450px',
  };

  return (
    <div className="support-layout-container">
      <SearchOptions
        SearchResult={SearchResultForCompany}
        onSelect={handleCompanySelect}
        placeholder="Search company history"
        value=""
      />

      <div style={containerStyle}>
        <div style={tableContainerStyle}>
          <Spin spinning={isFetchingCMP || isLoadingCMP} size="large">
            <Table
              size="small"
              onChange={onChangeCMP}
              dataSource={companyDataSource}
              columns={companyColumns}
              style={{ width: '450px' }}
              pagination={{
                total,
                pageSize: 100,
                showSizeChanger: false,
                current: typeof skip2 === 'string' ? parseInt(skip2) : skip2,
              }}
              scroll={{ y: 640 }}
            />
          </Spin>
        </div>

        <div>
          <SupportTable
            support={supportData?.data || []}
            total={supportData?.count || 0}
            onChange={handleSupportPagination}
            isLoading={isLoading}
            isFetching={isFetching}
            skip={supportPage}
          />
        </div>
      </div>
    </div>
  );
};

export default SupportLayout;
